"use client"

import { useMemo } from "react"
import { useInfiniteQuery } from "@tanstack/react-query"
import { withQueryClientScope } from "@/shared/scope"
import { Effect } from "effect"
import { RpcClient } from "@effect/rpc"
import { chatEq, ChatRpc } from "./api/chat-client"
import { chatKeys } from "./keys"
import { useChatScope } from "./use-chat-scope"
import type { ConversationSummaryView } from "@/contexts/chat/domain"

/**
 * Infinite (keyset) inbox of viewer-scoped conversations, newest first.
 * `nextCursor` is an opaque `lastMessageAt`-based keyset cursor from the RPC.
 */
export function useConversations() {
  const scope = useChatScope()
  const query = useInfiniteQuery({
    queryKey: [...chatKeys.conversations, scope],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam, client }) => {
      const effect = Effect.gen(function* () {
        const rpc = yield* RpcClient.make(ChatRpc)
        return yield* rpc.listConversations({ cursor: pageParam, limit: 30 })
      }).pipe(Effect.scoped, Effect.timeout("30 seconds"), Effect.provide(chatEq.layer), withQueryClientScope(client))
      return await Effect.runPromise(effect)
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 30_000,
  })

  const conversations = useMemo(
    () =>
      (query.data?.pages ?? []).flatMap(
        (p) => p.items as ReadonlyArray<ConversationSummaryView>,
      ),
    [query.data],
  )

  return { ...query, conversations }
}
